import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { WilayahCombobox } from "@/components/network/WilayahCombobox";
import { SELECT_CLS, apiError, notifyOdcSync } from "@/lib/network";

const byId = (o) => o.id;
const kodeNama = (o) => `${o.kode} — ${o.nama}`;

const SRC = [["pon", "OLT (port PON)"], ["odc", "ODC"], ["odp", "ODP"], ["splitter", "Output splitter lain"]];
const TGT = [["odc", "ODC"], ["odp", "ODP"], ["splitter", "Input splitter lain"]];
const LIST_URL = { odc: "/network/odc", odp: "/network/odp", splitter: "/network/splitters" };
const freeOutputs = (x) => Array.from({ length: x.jumlah_output }, (_, i) => i + 1).filter((n) => !x.outputs.some((o) => o.output_port === n));

function KindSelect({ value, onChange, options, testid }) {
  return (
    <select className={`${SELECT_CLS} w-full`} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
  );
}

// Daftar perangkat AKTIF per jenis; splitter dikecualikan dirinya sendiri (dan, bila perlu, yang sudah punya input).
// asTarget: sembunyikan tujuan yang sudah punya induk/feed aktif; sebagai sumber splitter: hanya yang masih punya output kosong.
function useDevices(kind, open, selfId, asTarget) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    setRows([]);
    if (!open || !kind || kind === "pon") return;
    Promise.all([api.get(LIST_URL[kind], { params: { status: "AKTIF" } }), asTarget && kind !== "splitter" ? api.get("/network/olt-map") : null])
      .then(([r, m]) => {
        const busy = new Set(m ? [...m.data.split_out.filter((o) => o.target_kind === kind).map((o) => o.target_id),
                                   ...(kind === "odc" ? m.data.links.map((l) => l.odc_id) : [])] : []);
        setRows(r.data.filter((x) => x.id !== selfId && !busy.has(x.id)
          && (kind !== "splitter" || (asTarget ? !x.input : x.output_terpakai < x.jumlah_output))));
      })
      .catch(() => toast.error("Gagal memuat daftar perangkat"));
  }, [kind, open, selfId, asTarget]);
  return rows;
}

export function SplitterInputDialog({ sp, onClose, onSaved }) {
  const [kind, setKind] = useState("pon");
  const [olts, setOlts] = useState([]);
  const [oltId, setOltId] = useState("");
  const [ports, setPorts] = useState([]);
  const [portId, setPortId] = useState("");
  const [srcId, setSrcId] = useState("");
  const [srcFree, setSrcFree] = useState([]);
  const [srcOut, setSrcOut] = useState("");
  const [catatan, setCatatan] = useState("");
  const devices = useDevices(kind, !!sp, sp?.id, false);
  useEffect(() => {
    if (sp) { setKind("pon"); setOltId(""); setPortId(""); setSrcId(""); setCatatan(""); api.get("/network/olt", { params: { status: "AKTIF" } }).then((r) => setOlts(r.data)).catch(() => toast.error("Gagal memuat OLT")); }
  }, [sp]);
  useEffect(() => { setSrcId(""); }, [kind]);
  useEffect(() => {
    setPortId(""); setPorts([]);
    if (oltId) api.get(`/network/olt/${oltId}`).then((r) => setPorts(r.data.ports.filter((p) => p.status === "AKTIF" && !p.link && !p.splitter_link))).catch(() => toast.error("Gagal memuat port PON"));
  }, [oltId]);
  useEffect(() => {
    setSrcFree([]); setSrcOut("");
    if (kind === "splitter" && srcId) api.get(`/network/splitters/${srcId}`).then((r) => { const f = freeOutputs(r.data); setSrcFree(f); setSrcOut(f[0] ? String(f[0]) : ""); })
      .catch(() => toast.error("Gagal memuat output splitter sumber"));
  }, [kind, srcId]);
  const ready = kind === "pon" ? !!portId : kind === "splitter" ? !!(srcId && srcOut) : !!srcId;
  const submit = async () => {
    const body = kind === "pon" ? { source_kind: "pon", pon_port_id: portId }
      : { source_kind: kind, source_id: srcId, ...(kind === "splitter" ? { source_output_port: Number(srcOut) } : {}) };
    try {
      const r = await api.post("/network/splitter-inputs", { splitter_id: sp.id, catatan, ...body });
      notifyOdcSync(r.data.odc_sync, toast);
      toast.success(`Input ${sp.kode} terhubung`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!sp} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="splitter-input-dialog">
        <DialogHeader><DialogTitle>Hubungkan input {sp?.kode}</DialogTitle></DialogHeader>
        <Label>Jenis sumber</Label>
        <KindSelect value={kind} onChange={setKind} options={SRC} testid="splitter-input-kind" />
        {kind === "pon" ? <>
          <Label>OLT (AKTIF)</Label>
          <WilayahCombobox className="w-full" options={olts} value={oltId} onChange={setOltId} testid="splitter-input-olt" placeholder="— Pilih OLT —"
                           getValue={byId} getLabel={kodeNama} searchPlaceholder="Cari OLT…" emptyText="OLT tidak ditemukan." />
          <Label>Port PON (kosong &amp; AKTIF)</Label>
          <select className={`${SELECT_CLS} w-full`} value={portId} onChange={(e) => setPortId(e.target.value)} disabled={!oltId} data-testid="splitter-input-port">
            <option value="">{oltId && !ports.length ? "Tidak ada port kosong" : "— Pilih port —"}</option>
            {ports.map((p) => <option key={p.id} value={p.id}>{p.nama_port}</option>)}</select>
        </> : <>
          <Label>{kind === "splitter" ? "Splitter sumber (AKTIF)" : `${kind.toUpperCase()} sumber (AKTIF)`}</Label>
          <WilayahCombobox className="w-full" options={devices} value={srcId} onChange={setSrcId} testid="splitter-input-source" placeholder="— Pilih perangkat —"
                           getValue={byId} getLabel={kodeNama} searchPlaceholder="Cari kode/nama…" emptyText="Perangkat tidak ditemukan." />
          {kind === "splitter" && <>
            <Label>Output splitter sumber</Label>
            <select className={`${SELECT_CLS} w-full`} value={srcOut} onChange={(e) => setSrcOut(e.target.value)} disabled={!srcId} data-testid="splitter-input-source-output">
              {!srcFree.length && <option value="">{srcId ? "Semua output terpakai" : "Pilih splitter dulu"}</option>}
              {srcFree.map((n) => <option key={n} value={n}>Output {n}</option>)}</select>
          </>}
        </>}
        <Label>Catatan</Label><Input value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="splitter-input-catatan" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button disabled={!ready} onClick={submit} data-testid="splitter-input-submit">Hubungkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SplitterOutputDialog({ target, onClose, onSaved }) {
  const [kind, setKind] = useState("odc");
  const [tid, setTid] = useState("");
  const [port, setPort] = useState("");
  const [catatan, setCatatan] = useState("");
  const sp = target?.sp;
  const free = sp ? freeOutputs(sp) : [];
  const devices = useDevices(kind, !!target, sp?.id, true);
  useEffect(() => { if (target) { setKind("odc"); setTid(""); setCatatan(""); setPort(String(target.port || "")); } }, [target]);
  useEffect(() => { setTid(""); }, [kind]);
  const label = TGT.find(([v]) => v === kind)?.[1];
  const submit = async () => {
    if (!port || !tid) return toast.error("Pilih output dan tujuan");
    try {
      const r = await api.post("/network/splitter-outputs", { splitter_id: sp.id, output_port: Number(port), target_kind: kind, target_id: tid, catatan });
      notifyOdcSync(r.data.odc_sync, toast);
      toast.success(`Output ${port} terhubung ke ${label}`);
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="splitter-output-dialog">
        <DialogHeader><DialogTitle>Hubungkan output {sp?.kode} ({sp?.rasio})</DialogTitle></DialogHeader>
        {sp?.input?.olt?.status === "PERBAIKAN" && (
          <div className="rounded-md border border-[#FCD34D] bg-[#FEF3C7] px-3 py-2 text-xs font-semibold text-[#92400E]" role="alert" data-testid="splitter-output-olt-repair-warning">
            OLT induk sedang PERBAIKAN. Koneksi fisik dapat dicatat, tetapi layanan mungkin terganggu.</div>
        )}
        <Label>Port output</Label>
        <select className={`${SELECT_CLS} w-full`} value={port} onChange={(e) => setPort(e.target.value)} data-testid="splitter-output-port">
          <option value="">{free.length ? "— Pilih output —" : "Semua output terpakai"}</option>
          {free.map((n) => <option key={n} value={n}>Output {n}</option>)}</select>
        <Label>Jenis tujuan</Label>
        <KindSelect value={kind} onChange={setKind} options={TGT} testid="splitter-output-kind" />
        <Label>{label} tujuan (AKTIF{kind === "splitter" ? ", belum punya input" : ""})</Label>
        <WilayahCombobox className="w-full" options={devices} value={tid} onChange={setTid} testid="splitter-output-target" placeholder="— Pilih tujuan —"
                         getValue={byId} getLabel={kodeNama} searchPlaceholder="Cari kode/nama…" emptyText="Perangkat tidak ditemukan." />
        <Label>Catatan</Label><Input value={catatan} onChange={(e) => setCatatan(e.target.value)} data-testid="splitter-output-catatan" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button disabled={!port || !tid} onClick={submit} data-testid="splitter-output-submit">Hubungkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SplitterEndDialog({ target, onClose, onSaved }) {
  const [alasan, setAlasan] = useState("");
  useEffect(() => setAlasan(""), [target]);
  const submit = async () => {
    try {
      await api.post(target.url, { alasan });
      toast.success("Relasi diputus (riwayat tetap tersimpan)");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="splitter-end-dialog">
        <DialogHeader><DialogTitle>Putus {target?.label}?</DialogTitle></DialogHeader>
        <p className="text-sm text-[#6B7280]">Relasi diakhiri dan disimpan sebagai riwayat. Data OLT, ODC, ODP, dan pelanggan tidak berubah.</p>
        <Label>Alasan</Label><Input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="min. 3 karakter" data-testid="splitter-end-alasan" />
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button>
          <Button variant="destructive" disabled={alasan.trim().length < 3} onClick={submit} data-testid="splitter-end-submit">Putus Relasi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
